import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { artUrl } from "@/lib/uploadArt";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import TagPicker from "@/components/studio/TagPicker";
import { FIXED_TAGS, tagClasses } from "@/lib/tags";
import { FolderArchive, Download, Trash2, Search, Clock, Infinity as InfIcon, FileText, AlertTriangle, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";

const RETENTION_META = {
  temporary: { label: "Temporary", cls: "bg-slate-100 text-slate-600", icon: FileText },
  "30day": { label: "30 days", cls: "bg-amber-100 text-amber-700", icon: Clock },
  permanent: { label: "Permanent", cls: "bg-emerald-100 text-emerald-700", icon: InfIcon },
};

const fmtSize = (b) => !b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;
const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
};

export default function Files() {
  const [files, setFiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [retention, setRetention] = useState("");
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (clientId) params.client_id = clientId;
    if (retention) params.retention = retention;
    if (tag) params.tag = tag;
    if (q) params.q = q;
    api.get("/library/files", { params }).then(({ data }) => setFiles(data)).catch(() => {}).finally(() => setLoading(false));
  }, [clientId, retention, tag, q]);

  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const tagOptions = Array.from(new Set([...FIXED_TAGS, ...files.flatMap((f) => f.tags || [])]));

  const changeRetention = async (f, val) => {
    try { await api.put(`/library/files/${f.file_id}`, { retention: val }); toast.success("Retention updated"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const changeTags = async (f, newTags) => {
    setFiles((fs) => fs.map((x) => x.file_id === f.file_id ? { ...x, tags: newTags } : x));
    try { await api.put(`/library/files/${f.file_id}`, { tags: newTags }); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); load(); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.filename}" from the library? This cannot be undone.`)) return;
    try { await api.delete(`/library/files/${f.file_id}`); toast.success("File deleted"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="files-page">
      <PageHeader eyebrow="Storage" title="File Library" subtitle="Every production file you save, organized by client, order and tag." testid="files-header" />

      <div className="p-8 space-y-5">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3" data-testid="files-filters">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search filename…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-56" data-testid="files-search" />
          </div>
          <select className="h-10 rounded-lg border px-2 text-sm bg-white" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="files-client-filter">
            <option value="">All clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="h-10 rounded-lg border px-2 text-sm bg-white" value={retention} onChange={(e) => setRetention(e.target.value)} data-testid="files-retention-filter">
            <option value="">All retention</option>
            <option value="temporary">Temporary</option>
            <option value="30day">30 days</option>
            <option value="permanent">Permanent</option>
          </select>
          <select className="h-10 rounded-lg border px-2 text-sm bg-white" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="files-tag-filter">
            <option value="">All tags</option>
            {tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="text-xs text-slate-400 font-mono ml-auto">{files.length} file{files.length === 1 ? "" : "s"}</span>
        </div>

        {loading ? (
          <div className="text-center text-slate-400 py-20">Loading…</div>
        ) : files.length === 0 ? (
          <div className="text-center text-slate-400 py-20" data-testid="files-empty">
            <FolderArchive size={40} className="mx-auto mb-3 opacity-50" />
            No files saved yet. Build a print-ready file in Print Studio and tap “Save to library”.
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="files-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] font-mono uppercase text-slate-400">
                <tr>
                  <th className="text-left px-4 py-2.5">File</th>
                  <th className="text-left px-4 py-2.5">Client</th>
                  <th className="text-left px-4 py-2.5">Tags</th>
                  <th className="text-left px-4 py-2.5">Retention</th>
                  <th className="text-left px-4 py-2.5">Saved</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => {
                  const rm = RETENTION_META[f.retention] || RETENTION_META.temporary;
                  const dl = daysLeft(f.expires_at);
                  return (
                    <tr key={f.file_id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`file-row-${f.file_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 truncate max-w-[220px]">{f.filename}</div>
                        {f.label && <div className="text-xs text-slate-400 truncate max-w-[220px]">{f.label}</div>}
                        <div className="text-[10px] text-slate-400 font-mono">{fmtSize(f.size)}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{f.client_name || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
                          {(f.tags || []).map((t) => (
                            <span key={t} className={`text-[10px] px-2 py-0.5 rounded-full border ${tagClasses(t)}`}>{t}</span>
                          ))}
                          <Popover>
                            <PopoverTrigger asChild>
                              <button className="text-slate-400 hover:text-[#2495D3] p-1 rounded" title="Edit tags" data-testid={`file-tags-${f.file_id}`}><TagIcon size={13} /></button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64" data-testid={`file-tags-pop-${f.file_id}`}>
                              <div className="text-[11px] font-mono uppercase text-slate-400 mb-2">Tags</div>
                              <TagPicker value={f.tags || []} onChange={(nt) => changeTags(f, nt)} testid={`file-tagpicker-${f.file_id}`} />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <select value={f.retention || "temporary"} onChange={(e) => changeRetention(f, e.target.value)}
                          className={`text-xs rounded-full px-2.5 py-1 border-0 font-semibold cursor-pointer ${rm.cls}`} data-testid={`file-retention-${f.file_id}`}>
                          <option value="temporary">Temporary</option>
                          <option value="30day">30 days</option>
                          <option value="permanent">Permanent</option>
                        </select>
                        {f.retention === "30day" && dl !== null && (
                          <div className={`text-[10px] mt-1 flex items-center gap-1 ${dl <= 7 ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                            {dl <= 7 && <AlertTriangle size={10} />}{dl > 0 ? `${dl} day${dl === 1 ? "" : "s"} left` : "Expired"}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">{f.saved_at ? new Date(f.saved_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <a href={artUrl(f.file_id)} target="_blank" rel="noreferrer" download>
                            <Button size="sm" variant="outline" data-testid={`file-download-${f.file_id}`}><Download size={14} /></Button>
                          </a>
                          <button onClick={() => remove(f)} className="text-slate-400 hover:text-red-500 p-1.5" data-testid={`file-delete-${f.file_id}`}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
